/**
 * acadadminGapsBuilder — sub-container dos gaps do acadadmin (fase "Admin Completo").
 *
 * Agrega slices que faltavam no admin acadêmico: Formulário (G.5.14), CPA (G.5.13),
 * Requerimento (G.5.15), Templates (G.5.18) e AgendamentoParticipante (G.5.17).
 * Mantém o `acadadminBuilder` (G.5.1..G.5.12) intocado.
 *
 * @since G.5.14
 */
import type { $Fetch } from 'ofetch';

import { HttpFormularioGateway } from '@/infrastructure/http/HttpFormularioGateway';
import { HttpCpaGateway } from '@/infrastructure/http/HttpCpaGateway';
import { HttpRequerimentoAdminGateway } from '@/infrastructure/http/HttpRequerimentoAdminGateway';
import {
  HttpContratoTipoGateway, HttpContratoTemplateGateway, HttpCertificadoTemplateGateway,
} from '@/infrastructure/http/HttpTemplateGateways';
import {
  HttpAgendamentoParticipanteGateway, HttpDisciplineDocentesGateway,
} from '@/infrastructure/http/HttpAgendamentoParticipanteGateway';

import {
  ListFormularios, GetFormulario, CreateFormulario, UpdateFormulario,
  PublicarFormulario, ClonarFormulario, InativarFormulario,
  ListFormularioPerguntas, AddFormularioPergunta, RemoverFormularioPergunta,
} from '@/application/usecase/FormularioUseCaseImpl';
import {
  ListCpaCiclos, GetCpaCiclo, CriarCpaCiclo, AgendarCpaCiclo, ProrrogarCpaCiclo,
  FecharCpaCiclo, ArquivarCpaCiclo, RelatorioSinteticoCpa, ListCpaDimensoes,
} from '@/application/usecase/CpaUseCaseImpl';
import {
  ListRequerimentoTipos, CreateRequerimentoTipo, UpdateRequerimentoTipo, DeactivateRequerimentoTipo,
  ListRequerimentos, GetRequerimento, ListRequerimentoHistorico, FilaRequerimento,
  ClaimRequerimentoEtapa, DecidirRequerimentoEtapa, PedirComplementacaoRequerimento,
  ReenviarRequerimento, ReverterRequerimento, GetRequerimentoPagamento, ConfirmarRequerimentoPagamento,
} from '@/application/usecase/RequerimentoAdminUseCaseImpl';
import {
  ListContratoTipos, CreateContratoTipo, UpdateContratoTipo, DeactivateContratoTipo,
  ListContratoTemplates, GetContratoTemplate, CreateContratoTemplate, UpdateContratoTemplate, DeactivateContratoTemplate,
  ListCertificadoTemplates, GetCertificadoTemplate, CreateCertificadoTemplate, UpdateCertificadoTemplate, DeactivateCertificadoTemplate,
} from '@/application/usecase/TemplateUseCaseImpl';
import {
  ListParticipantes, AdicionarParticipante, ConfirmarParticipante,
  CancelarParticipante, RegistrarPresenca, ListDocentesByDiscipline,
} from '@/application/usecase/AgendamentoParticipanteUseCaseImpl';

export function buildAcadadminGapsSlice(httpClient: $Fetch) {
  const formularioGateway = new HttpFormularioGateway(httpClient);
  const cpaGateway = new HttpCpaGateway(httpClient);
  const requerimentoAdminGateway = new HttpRequerimentoAdminGateway(httpClient);
  const contratoTipoGateway = new HttpContratoTipoGateway(httpClient);
  const contratoTemplateGateway = new HttpContratoTemplateGateway(httpClient);
  const certificadoTemplateGateway = new HttpCertificadoTemplateGateway(httpClient);
  const participanteGateway = new HttpAgendamentoParticipanteGateway(httpClient);
  const disciplineDocentesGateway = new HttpDisciplineDocentesGateway(httpClient);

  const useCases = {
    listFormularios: new ListFormularios(formularioGateway),
    getFormulario: new GetFormulario(formularioGateway),
    createFormulario: new CreateFormulario(formularioGateway),
    updateFormulario: new UpdateFormulario(formularioGateway),
    publicarFormulario: new PublicarFormulario(formularioGateway),
    clonarFormulario: new ClonarFormulario(formularioGateway),
    inativarFormulario: new InativarFormulario(formularioGateway),
    listFormularioPerguntas: new ListFormularioPerguntas(formularioGateway),
    addFormularioPergunta: new AddFormularioPergunta(formularioGateway),
    removerFormularioPergunta: new RemoverFormularioPergunta(formularioGateway),
    listCpaCiclos: new ListCpaCiclos(cpaGateway),
    getCpaCiclo: new GetCpaCiclo(cpaGateway),
    criarCpaCiclo: new CriarCpaCiclo(cpaGateway),
    agendarCpaCiclo: new AgendarCpaCiclo(cpaGateway),
    prorrogarCpaCiclo: new ProrrogarCpaCiclo(cpaGateway),
    fecharCpaCiclo: new FecharCpaCiclo(cpaGateway),
    arquivarCpaCiclo: new ArquivarCpaCiclo(cpaGateway),
    relatorioSinteticoCpa: new RelatorioSinteticoCpa(cpaGateway),
    listCpaDimensoes: new ListCpaDimensoes(cpaGateway),
    listRequerimentoTipos: new ListRequerimentoTipos(requerimentoAdminGateway),
    createRequerimentoTipo: new CreateRequerimentoTipo(requerimentoAdminGateway),
    updateRequerimentoTipo: new UpdateRequerimentoTipo(requerimentoAdminGateway),
    deactivateRequerimentoTipo: new DeactivateRequerimentoTipo(requerimentoAdminGateway),
    listRequerimentos: new ListRequerimentos(requerimentoAdminGateway),
    getRequerimento: new GetRequerimento(requerimentoAdminGateway),
    listRequerimentoHistorico: new ListRequerimentoHistorico(requerimentoAdminGateway),
    filaRequerimento: new FilaRequerimento(requerimentoAdminGateway),
    claimRequerimentoEtapa: new ClaimRequerimentoEtapa(requerimentoAdminGateway),
    decidirRequerimentoEtapa: new DecidirRequerimentoEtapa(requerimentoAdminGateway),
    pedirComplementacaoRequerimento: new PedirComplementacaoRequerimento(requerimentoAdminGateway),
    reenviarRequerimento: new ReenviarRequerimento(requerimentoAdminGateway),
    reverterRequerimento: new ReverterRequerimento(requerimentoAdminGateway),
    getRequerimentoPagamento: new GetRequerimentoPagamento(requerimentoAdminGateway),
    confirmarRequerimentoPagamento: new ConfirmarRequerimentoPagamento(requerimentoAdminGateway),
    listContratoTipos: new ListContratoTipos(contratoTipoGateway),
    createContratoTipo: new CreateContratoTipo(contratoTipoGateway),
    updateContratoTipo: new UpdateContratoTipo(contratoTipoGateway),
    deactivateContratoTipo: new DeactivateContratoTipo(contratoTipoGateway),
    listContratoTemplates: new ListContratoTemplates(contratoTemplateGateway),
    getContratoTemplate: new GetContratoTemplate(contratoTemplateGateway),
    createContratoTemplate: new CreateContratoTemplate(contratoTemplateGateway),
    updateContratoTemplate: new UpdateContratoTemplate(contratoTemplateGateway),
    deactivateContratoTemplate: new DeactivateContratoTemplate(contratoTemplateGateway),
    listCertificadoTemplates: new ListCertificadoTemplates(certificadoTemplateGateway),
    getCertificadoTemplate: new GetCertificadoTemplate(certificadoTemplateGateway),
    createCertificadoTemplate: new CreateCertificadoTemplate(certificadoTemplateGateway),
    updateCertificadoTemplate: new UpdateCertificadoTemplate(certificadoTemplateGateway),
    deactivateCertificadoTemplate: new DeactivateCertificadoTemplate(certificadoTemplateGateway),
    listParticipantes: new ListParticipantes(participanteGateway),
    adicionarParticipante: new AdicionarParticipante(participanteGateway),
    confirmarParticipante: new ConfirmarParticipante(participanteGateway),
    cancelarParticipante: new CancelarParticipante(participanteGateway),
    registrarPresenca: new RegistrarPresenca(participanteGateway),
    listDocentesByDiscipline: new ListDocentesByDiscipline(disciplineDocentesGateway),
  };

  return { useCases };
}
